import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';

import { Subject, takeUntil } from 'rxjs';

import { CardValidatorHelper } from '../../../helpers/card-validator-helper';
import { Utility } from 'src/app/helpers/utility-helper';

import { CreditCardService, TokenErrorReasonCode } from 'src/app/services/credit-card/credit-card.service';
import { Payment } from 'src/app/models/payment';
import { translate } from '@jsverse/transloco';
import { CountryCodes } from '../../../models/country-codes.enum';

declare global {
  interface Window {
    cardForm: any;
  }
}

@Component({
  selector: 'app-credit-card-form',
  templateUrl: './credit-card-form.component.html',
  styleUrls: ['./credit-card-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreditCardFormComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @Input() amount: string | undefined = '0';
  @Output() submitForm: EventEmitter<any> = new EventEmitter();
  @Output() buttonClick: EventEmitter<any> = new EventEmitter();

  visibleForm = false;
  iframesCardHolderNameError = false;
  iframesCardNumberError = false;
  iframesCardExpirationError = false;
  iframesCardCvvError = false;
  invoiceRegionType = translate('State');

  @ViewChild('indexRequestedAmount') indexRequestedAmount!: ElementRef;
  @ViewChild('indexBillingAddress1') indexBillingAddress1!: ElementRef;
  @ViewChild('indexBillingAddress2') indexBillingAddress2!: ElementRef;
  @ViewChild('indexCity') indexCity!: ElementRef;
  @ViewChild('indexState') indexState!: ElementRef;
  @ViewChild('indexAddressBillingZip') indexAddressBillingZip!: ElementRef;

  public paymentData!: Payment;
  private unsubscribe$ = new Subject<any>();

  balanceDueFixedPointFormat = '0';
  balanceDueCurrencyFormat = '0';
  decimalSeparator = Utility.DefaultDecimalSeparator;
  invoiceCountryCode = '';

  constructor(
    private creditCardService: CreditCardService,
    private renderer: Renderer2,
    private detectRef: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.initializeCardForm();
  }

  handleClearAddressClick(): void {
    this.paymentData = {
      ...this.paymentData,
      StreetAddress1: '',
      StreetAddress2: '',
      City: '',
      StateCode: '',
      PostalCode: '',
    };
  }

  ngAfterViewInit(): void {
    this.createCardForm();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  private initializeCardForm(): void {
    this.paymentData = this.creditCardService.getInitialPayment(this.amount || '0');
    this.balanceDueFixedPointFormat = this.creditCardService.getBalanceDue();
    this.balanceDueCurrencyFormat = this.creditCardService.getFormattedBalanceDue();
    this.decimalSeparator = this.creditCardService.getCurrentCultureDecimalSeparator();
    this.invoiceCountryCode = this.creditCardService.getInvoiceCountryCode();

    if (this.invoiceCountryCode == CountryCodes.US) {
      this.invoiceRegionType = translate('State');
    }
    else if (this.invoiceCountryCode == CountryCodes.CA) {
      this.invoiceRegionType = translate('Province');
    }
  }

  private createCardForm = async () => {
    const cardForm = await this.creditCardService.getCreditCardForm(
      this.paymentData.CardholderName
    );
    this.configureCardForm(cardForm);
  };

  private configureCardForm(cardForm: any): void {
    cardForm.ready(() => {
      this.visibleForm = true;
      this.detectRef.markForCheck();
    });

    this.addCardTokenSuccessEvent(cardForm);
    this.addCardTokenErrorEvent(cardForm);
    this.addPaySubmitEvent(cardForm);
  }

  private addCardTokenSuccessEvent(cardForm: any): void {
    cardForm.on('card-number', 'token-success', async (response: any) => {

      this.hideAllCardFormErrors();

      const isValidResponse = this.validateTokenSuccessResponse(response);

      const isValidNonIframeFileds = this.validateNonIframeFileds();

      if (isValidResponse && isValidNonIframeFileds) {
        await cardForm.frames["card-cvv"].getCvv().then((cvv: any) => {
          this.paymentData.CardVerificationValue = cvv;
        });
        this.paymentData = this.creditCardService.setPaymentDataBeforeTransaction(this.paymentData, response);
        this.excecuteTransaction();
      }
      else {
        this.buttonClick.emit(false);
      }

      this.detectRef.markForCheck();
    });
  }

  private addCardTokenErrorEvent(cardForm: any): void {
    cardForm.on('token-error', async (response: any) => {

      await Utility.Timeout(1);

      this.hideAllCardFormErrors();

      this.buttonClick.emit(false);

      this.validateNonIframeFileds();

      this.validateTokenErrorResponse(response);

      this.detectRef.markForCheck();
    });
  }

  private addPaySubmitEvent(cardForm: any): void {
    cardForm.on('submit', 'click', () => {

      this.hideAllCardFormErrors();

      this.buttonClick.emit(true);
    });
  }

  private excecuteTransaction(): void {
    this.creditCardService
      .excecuteSaleTransaction(this.paymentData)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((response: any) => {
        this.submitForm.emit(response);
      });
  }

  private showErrorOnTokenFail(reason: any) {

    switch (reason.code) {
      case TokenErrorReasonCode.CardHolder: {
        this.iframesCardHolderNameError = true;
        break;
      }
      case TokenErrorReasonCode.CardNumber: {
        this.iframesCardNumberError = true;
        break;
      }
      case TokenErrorReasonCode.CardExpiration: {
        this.iframesCardExpirationError = true;
        break;
      }
      case TokenErrorReasonCode.CardCVVPortico:
      case TokenErrorReasonCode.CardCVVTransIT: {
        this.iframesCardCvvError = true;
        break;
      }
    }
  }

  private hideAllCardFormErrors(): void {
    this.iframesCardHolderNameError = false;
    this.iframesCardNumberError = false;
    this.iframesCardExpirationError = false;
    this.iframesCardCvvError = false;

    this.renderer.setProperty(this.indexRequestedAmount.nativeElement, 'error', '');
    this.renderer.setProperty(this.indexBillingAddress1.nativeElement, 'error', '');
    this.renderer.setProperty(this.indexCity.nativeElement, 'error', '');
    this.renderer.setProperty(this.indexState.nativeElement, 'error', '');
    this.renderer.setProperty(this.indexAddressBillingZip.nativeElement, 'error', '');
  }

  private validateTokenErrorResponse(response: any) {
    if (response?.error) {
      response.reasons.forEach((reason: any) => {
        this.showErrorOnTokenFail(reason);
      });
    }
  }

  private validateTokenSuccessResponse(response: any) {

    let isValid = true;

    if (!response.details.cardholderName) {
      this.setCardholderNameError();

      isValid = false;
    }

    if (!response.details.cardNumber) {
      this.setCardNumberError();

      isValid = false;
    }

    if (!response.details.expiryMonth || !response.details.expiryYear) {
      this.setCardExpiryError();

      isValid = false;
    }

    if (!response.details.cardSecurityCode) {
      this.setCardSecurityCodeError();

      isValid = false;
    }

    if (!response.paymentReference) { // for GPAPI, we can receive a 'token-success' for things like an invalid card number, however the paymentReference would be null
      isValid = false;
    }

    return isValid;
  }

  private validateNonIframeFileds(): boolean {

    let isValid = true;

    // amount validation
    if (!Utility.stringIsNotNullOrEmpty(this.paymentData?.RequestedAmount)) {
      this.setAmountValidationError();

      isValid = false;
    }
    else if (!CardValidatorHelper.IsPostiveDecimalAmount(this.paymentData?.RequestedAmount, this.decimalSeparator)) {
      this.setAmountFormatValidationError();

      isValid = false;
    }
    else if (!CardValidatorHelper.IsValidRequestedAmount(this.paymentData?.RequestedAmount, this.balanceDueFixedPointFormat, this.decimalSeparator)) {
      this.setAmountValidationError();

      isValid = false;
    }

    // postal code validation
    if (this.invoiceCountryCode == CountryCodes.US && !CardValidatorHelper.UsPostalCodeRegx.test(this.paymentData.PostalCode) || this.invoiceCountryCode == CountryCodes.CA && !CardValidatorHelper.CaPostalCodeRegx.test(this.paymentData.PostalCode) || !Utility.stringIsNotNullOrEmpty(this.paymentData.PostalCode)) {
      this.setPostalCodeRequiredValidationError();

      isValid = false;
    }

    if (!Utility.stringIsNotNullOrEmpty(this.paymentData?.StreetAddress1)) {
      this.setBillingAddress1Error();

      isValid = false;
    }

    if (!Utility.stringIsNotNullOrEmpty(this.paymentData?.City)) {
      this.setCityValidationError();

      isValid = false;
    }

    if (!Utility.stringIsNotNullOrEmpty(this.paymentData?.StateCode)) {
      this.setStateValidationError();

      isValid = false;
    }

    return isValid;
  }

  private setCardholderNameError = () =>
    (() =>
      this.iframesCardHolderNameError = true
    )();

  private setCardNumberError = () =>
    (() =>
      this.iframesCardNumberError = true
    )();

  private setCardExpiryError = () =>
    (() =>
      this.iframesCardExpirationError = true
    )();

  private setCardSecurityCodeError = () =>
    (() =>
      this.iframesCardCvvError = true
    )();

  private setBillingAddress1Error = () =>
    (() =>
      this.renderer.setProperty(
        this.indexBillingAddress1.nativeElement,
        'error',
        translate('StreetAddressRequired')
      ))();

  private setPostalCodeRequiredValidationError = () =>
    (() =>
      this.renderer.setProperty(
        this.indexAddressBillingZip.nativeElement,
        'error',
        translate('PostalCodeRequired')
      ))();

  private setStateValidationError = () =>
    (() => {
      var errorText = translate('StateRequired');

      if (this.invoiceCountryCode == CountryCodes.CA) {
        errorText = translate('ProvinceRequired');
      }

      this.renderer.setProperty(
        this.indexState.nativeElement,
        'error',
        errorText);
    })();

  private setCityValidationError = () =>
    (() =>
      this.renderer.setProperty(
        this.indexCity.nativeElement,
        'error',
        translate('CityRequired')
      ))();

  private setAmountValidationError = () =>
    (() => {
      const error = translate('AmountRequired', { value1: 0, value2: this.balanceDueFixedPointFormat });

      this.renderer.setProperty(
        this.indexRequestedAmount.nativeElement,
        'error',
        error
      );
    })();

  private setAmountFormatValidationError = () =>
    (() => {
      const error = translate('AmountFormatError', { value1: this.decimalSeparator });

      this.renderer.setProperty(
        this.indexRequestedAmount.nativeElement,
        'error',
        error
      );
    })();
}
